import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import "./css/App.css";

import axiosInstance, { getAccessToken, tokenEvents } from "./api/axios";
import { useAuth } from "./contexts/AuthContext";
import { useSoundContext } from "./contexts/SoundContext";
import io from "socket.io-client";
import { v4 as uuidv4 } from "uuid";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Slide } from "react-toastify";
import SomeChart from "./components/SomeChart";
import { MenuIcon, MinusIcon, Plus, PlusIcon } from "lucide-react";
import CrashHistoryRow from "./components/cashHistroy";
import CoinsAnimation from "./components/CoinsAnimation";
import CountdownAnimation from "./components/CountdownAnimation";
import ActiveBetRow from "./components/ActiveBetRow";
import * as tactus from "tactus";
const triggerHaptic = (duration) => {
  if (typeof tactus.triggerHaptic === "function") {
    tactus.triggerHaptic(duration);
  } else if (
    tactus.default &&
    typeof tactus.default.triggerHaptic === "function"
  ) {
    tactus.default.triggerHaptic(duration);
  } else {
    console.warn("triggerHaptic is not a function", tactus);
  }
};

function App() {
  const {
    userData,
    setUserData,
    login: authLogin,
    register: authRegister,
    logout: authLogout,
    fetchUser,
    loading,
  } = useAuth();
  const {
    playSoundEffect,
    playBackgroundMusic,
    stopBackgroundMusic,
    audioUnlocked,
  } = useSoundContext();
  const [lastQuickBetValue, setLastQuickBetValue] = useState(null);
  const [showCoinsAnimation, setShowCoinsAnimation] = useState(false);
  const [coinStartPos, setCoinStartPos] = useState({ x: 0, y: 0 });
  const [coinEndPos, setCoinEndPos] = useState({ x: 0, y: 0 });

  const balanceRef = useRef(null);
  const betBtnRef = useRef(null);

  const [autoCashoutEnabled, setAutoCashoutEnabled] = useState(false); // default checked
  const [mode, setMode] = useState("manual");
  const [viewMode, setViewMode] = useState("all");
  const [registerUsername, setRegisterUsername] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [profitDisplay, setProfitDisplay] = useState(null);
  const [loginUsername, setLoginUsername] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [betAmount, setBetAmount] = useState(
    localStorage.getItem("local_storage_wager") || 100,
  );
  const [autoPayoutMultiplier, setAutoPayoutMultiplier] = useState(
    localStorage.getItem("local_storage_multiplier") || 1.2,
  );
  const [multiplier, setMultiplier] = useState(null);
  const [liveMultiplier, setLiveMultiplier] = useState("CONNECTING...");
  const [liveMultiplierSwitch, setLiveMultiplierSwitch] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const [globalSocket, setGlobalSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [betActive, setBetActive] = useState(false);
  const [betPending, setBetPending] = useState(false);
  const [cancelPending, setCancelPending] = useState(false);
  const [cashoutPending, setCashoutPending] = useState(false);
  const [crashHistory, setCrashHistory] = useState([]);
  const [roundIdList, setRoundIdList] = useState([]);
  const [bBettingPhase, setbBettingPhase] = useState(false);
  const [bettingPhaseTime, setBettingPhaseTime] = useState(-1);
  const [bBetForNextRound, setbBetForNextRound] = useState(false);
  const [messageToTextBox, setMessageToTextBox] = useState("");
  const [chatHistory, setChatHistory] = useState();
  const [liveBettingTable, setLiveBettingTable] = useState([]);
  const [activePlayerCount, setActivePlayerCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [globalTimeNow, setGlobalTimeNow] = useState(0);
  const [chartData, setChartData] = useState({ datasets: [] });
  const [chartOptions, setChartOptions] = useState({});
  const [chartSwitch, setChartSwitch] = useState(false);
  const [gamePhaseTimeElapsed, setGamePhaseTimeElapsed] = useState();
  const [randomSeedPlaneAnimation, setRandomSeedPlaneAnimation] = useState();
  const [startTime, setStartTime] = useState();
  const [streakList, setStreakList] = useState([]);
  const [tenNumbers, setTenNumbers] = useState([0, 1, 2, 3, 4, 5, 6, 7, 8, 9]);
  const [isInputOneFocusVisible, setIsInputOneFocusVisible] = useState(false);
  const [isInputTwoFocusVisible, setIsInputTwoFocusVisible] = useState(false);

  const multiplierCount = useRef([]);
  const timeCount_xaxis = useRef([]);
  const realCounter_yaxis = useRef(5);
  const bBetForNextRoundRef = useRef(false);
  const betAmountRef = useRef(100);
  const autoPayoutMultiplierRef = useRef(2);

  // Keep refs in sync with state for socket handler
  useEffect(() => {
    bBetForNextRoundRef.current = bBetForNextRound;
  }, [bBetForNextRound]);

  useEffect(() => {
    betAmountRef.current = betAmount;
    autoPayoutMultiplierRef.current = autoPayoutMultiplier;
  }, [betAmount, autoPayoutMultiplier]);

  // Sync background music when audio unlocks mid-round
  useEffect(() => {
    if (audioUnlocked && liveMultiplierSwitch) {
      // If audio just unlocked and a round is active, start background music
      playBackgroundMusic(true, 500);
    }
  }, [audioUnlocked, liveMultiplierSwitch, playBackgroundMusic]);

  // Socket.io setup
  useEffect(() => {
    if (loading) return; // Wait for auth state to be determined

    retrieve();
    const socket = io.connect(
      process.env.REACT_APP_BACKEND_URL || "http://localhost:4000",
      {
        auth: {
          token: getAccessToken(),
        },
        withCredentials: true,
      },
    );
    setGlobalSocket(socket);

    // Listen for token refresh and update socket auth
    const handleTokenRefresh = (event) => {
      socket.auth = { token: event.detail.token };
    };
    tokenEvents.addEventListener("tokenRefreshed", handleTokenRefresh);

    // Connection status handlers
    socket.on("connect", () => {
      console.log("Socket connected");
      setIsConnected(true);
      setLiveMultiplier("CONNECTED");
    });

    socket.on("disconnect", (reason) => {
      console.log("Socket disconnected:", reason);
      setIsConnected(false);
      setLiveMultiplier("DISCONNECTED");
      setLiveMultiplierSwitch(false); // Stop the counter
      setbBettingPhase(false); // Stop betting phase timer
    });

    socket.on("connect_error", (error) => {
      console.log("Connection error:", error);
      setIsConnected(false);
      setLiveMultiplier("CONNECTION ERROR");
    });

    socket.on("news_by_server", function (data) {
      setAnnouncement(data);
    });

    socket.on("start_multiplier_count", function (data) {
      playSoundEffect("round-start");
      playBackgroundMusic(true, 500);
      setGlobalTimeNow(Date.now());
      setLiveMultiplierSwitch(true);
      setbBettingPhase(false);
    });

    socket.on("stop_multiplier_count", function (data) {
      playSoundEffect("round-end");
      stopBackgroundMusic(500);
      setLiveMultiplier(data);
      setLiveMultiplierSwitch(false);

      setBetActive(false);
    });

    socket.on("update_user", function (data) {
      getUser();
    });

    socket.on("crash_history", function (data) {
      setCrashHistory(data);

      let temp_streak_list = [];
      const new_data = data;
      let blue_counter = 0;
      let red_counter = 0;

      for (let i = 0; i < data.length; i++) {
        if (new_data[i] >= 2) {
          blue_counter += 1;
          red_counter = 0;
          temp_streak_list.push(blue_counter);
        } else {
          red_counter += 1;
          blue_counter = 0;
          temp_streak_list.push(red_counter);
        }
      }
      setStreakList(temp_streak_list.reverse());
    });

    socket.on("get_round_id_list", function (data) {
      setRoundIdList(data.reverse());
    });

    socket.on("start_betting_phase", function (data) {
      setGlobalTimeNow(Date.now());
      setLiveMultiplier("Starting...");
      setbBettingPhase(true);
      setLiveBettingTable([]);

      // Read from refs to avoid stale closure
      if (bBetForNextRoundRef.current) {
        // Set betActive immediately to avoid UI flicker
        setBetActive(true);
        setbBetForNextRound(false);

        axiosInstance
          .post("/send_bet", {
            bet_amount: betAmountRef.current,
            payout_multiplier: autoPayoutMultiplierRef.current,
          })
          .then((res) => {
            if (res.data.success) {
              fetchUser();
            }
          })
          .catch((err) => {
            console.error(
              "Auto-bet failed:",
              err.response?.data || err.message,
            );
            // If bet failed, reset betActive
            setBetActive(false);
          });
      }

      retrieve_active_bettors_list();
      multiplierCount.current = [];
      timeCount_xaxis.current = [];
    });

    socket.on("receive_message_for_chat_box", (data) => {
      get_chat_history();
    });

    socket.on("receive_live_betting_table", (data) => {
      // Parse once and store parsed data
      const parsedData = JSON.parse(data);
      setLiveBettingTable(parsedData);
      setTenNumbers(Array(10 - parsedData.length).fill(2));

      // Check if current user has an active bet and restore betActive state
      // This handles reconnection during an active round
      if (userData?.username) {
        const userActiveBet = parsedData.find(
          (bet) =>
            bet.the_username === userData.username && bet.b_bet_live === true,
        );
        if (userActiveBet) {
          setBetActive(true);
        }
      }
    });

    socket.on("active_player_count", (count) => {
      setActivePlayerCount(count);
    });

    return () => {
      tokenEvents.removeEventListener("tokenRefreshed", handleTokenRefresh);
      socket.disconnect();
    };
  }, [loading]);

  // Calculate random users for avatar display
  const randomAvatarUsers = useMemo(() => {
    if (liveBettingTable && liveBettingTable.length > 0) {
      const shuffled = [...liveBettingTable].sort(() => Math.random() - 0.5);
      return shuffled.slice(0, Math.min(3, liveBettingTable.length));
    }
    return [];
  }, [liveBettingTable]);

  // Memoized sorted betting list - recalculates only when data or username changes
  const sortedBettingTable = useMemo(() => {
    if (!liveBettingTable || liveBettingTable.length === 0) {
      return [];
    }

    return [...liveBettingTable].sort((a, b) => {
      // Current user's bet always goes first
      if (a.the_username === userData?.username) return -1;
      if (b.the_username === userData?.username) return 1;
      return 0;
    });
  }, [liveBettingTable, userData?.username]);

  // Define useEffects
  useEffect(() => {
    // Auto mode: only auto cashout if switch is checked
    if (
      betActive &&
      mode === "auto" &&
      autoCashoutEnabled &&
      autoPayoutMultiplier <= liveMultiplier
    ) {
      auto_cashout_early();
      setBetActive(false);
    }
  }, [liveMultiplier]);

  const buttonClick = () => {
    globalSocket.emit("clicked", { message2: Date.now() });
  };

  useEffect(() => {
    let gameCounter = null;
    if (liveMultiplierSwitch && isConnected) {
      setLiveMultiplier("1.00");
      setRandomSeedPlaneAnimation(Math.random());

      gameCounter = setInterval(() => {
        let time_elapsed = (Date.now() - globalTimeNow) / 1000.0;
        setGamePhaseTimeElapsed(time_elapsed);
        setLiveMultiplier((1.0024 * Math.pow(1.098, time_elapsed)).toFixed(2));

        if (multiplierCount.current.length < 1) {
          multiplierCount.current.push(1);
          timeCount_xaxis.current.push(0);
        }
        multiplierCount.current.push(1.0024 * Math.pow(1.098, time_elapsed));
        timeCount_xaxis.current.push(time_elapsed);

        realCounter_yaxis.current += 1;
      }, 33); // Synced with chart render rate (~30 FPS)
    }
    return () => {
      clearInterval(gameCounter);
    };
  }, [liveMultiplierSwitch, isConnected]);

  useEffect(() => {
    let bettingInterval = null;

    if (bBettingPhase && isConnected) {
      bettingInterval = setInterval(() => {
        let time_elapsed = (Date.now() - globalTimeNow) / 1000.0;
        let time_remaining = (4 - time_elapsed).toFixed(2);
        setBettingPhaseTime(time_remaining);
      }, 10);
    }
    return () => {
      clearInterval(bettingInterval);
      setBettingPhaseTime("Starting...");
    };
  }, [bBettingPhase, isConnected]);

  useEffect(() => {
    localStorage.setItem("local_storage_wager", betAmount);
    localStorage.setItem("local_storage_multiplier", autoPayoutMultiplier);
  }, [betAmount, autoPayoutMultiplier]);

  useEffect(() => {
    get_game_status();
    setChartSwitch(true);
    setStartTime(Date.now());
    let getChatHistoryTimer = setTimeout(() => get_chat_history(), 1000);
    let getActiveBettorsTimer = setTimeout(
      () => retrieve_active_bettors_list(),
      1000,
    );
    let getBetHistory = setTimeout(() => retrieve_bet_history(), 1000);

    return () => {
      clearTimeout(getChatHistoryTimer);
      clearTimeout(getActiveBettorsTimer);
      clearTimeout(getBetHistory);
      // Cleanup button intervals
      if (betIntervalRef.current) clearInterval(betIntervalRef.current);
      if (betTimeoutRef.current) clearTimeout(betTimeoutRef.current);
      if (multiplierIntervalRef.current)
        clearInterval(multiplierIntervalRef.current);
      if (multiplierTimeoutRef.current)
        clearTimeout(multiplierTimeoutRef.current);
    };
  }, []);

  // Routes

  const getUser = async () => {
    await fetchUser();
  };

  const multiply = () => {
    axiosInstance.get("/multiply").then((res) => {
      if (res.data) {
        fetchUser();
      }
    });
  };

  const generate = () => {
    axiosInstance.get("/generate_crash_value").then((res) => {
      setMultiplier(res.data);
    });
  };

  const retrieve = () => {
    axiosInstance
      .get("/retrieve")
      .then((res) => {
        setMultiplier(res.data);
      })
      .catch((err) => {
        console.log("Retrieve error (ignoring):", err.message);
        // Ignore errors on initial load
      });
  };

  const send_bet = () => {
    // Check if bet amount exceeds balance
    if (Number(betAmount) > Number(userData.balance)) {
      setErrorMessage("Insufficient balance");
      return;
    }

    // Check socket connection
    if (!globalSocket || !isConnected) {
      setErrorMessage("Not connected to server");
      return;
    }

    setErrorMessage(""); // Clear any previous error messages
    playSoundEffect("bet-place");
    setBetPending(true);

    // Timeout for socket response (5 seconds)
    let timeoutId = setTimeout(() => {
      setBetPending(false);
      setErrorMessage("Request timeout - please try again");
    }, 5000);

    // Emit socket event with acknowledgment callback
    globalSocket.emit(
      "place_bet",
      {
        bet_amount: betAmount,
        payout_multiplier: autoPayoutMultiplier,
      },
      (response) => {
        clearTimeout(timeoutId);
        setBetPending(false);

        if (response.success) {
          setBetActive(true);
          setUserData((prev) => ({ ...prev, balance: response.balance }));
        } else {
          setErrorMessage(response.error || "Bet failed");
          console.error("Bet failed:", response.error);
        }
      },
    );
  };

  const cancel_bet = () => {
    // Prevent spam clicking
    if (cancelPending) {
      return;
    }

    // Check socket connection
    if (!globalSocket || !isConnected) {
      setErrorMessage("Not connected to server");
      return;
    }

    setCancelPending(true);

    // Timeout for socket response (5 seconds)
    let timeoutId = setTimeout(() => {
      setCancelPending(false);
      setErrorMessage("Request timeout - please try again");
    }, 5000);

    // Emit socket event with acknowledgment callback
    globalSocket.emit("cancel_bet", (response) => {
      clearTimeout(timeoutId);
      setCancelPending(false);

      if (response.success) {
        playSoundEffect("bet-cancel");
        setBetActive(false);
        setUserData((prev) => ({ ...prev, balance: response.balance }));
        setErrorMessage(""); // Clear any error messages
      } else {
        setErrorMessage(response.error || "Cancel failed");
        console.error("Cancel bet failed:", response.error);
      }
    });
  };

  const calculate_winnings = () => {
    axiosInstance.get("/calculate_winnings").then((res) => {
      getUser();
    });
  };

  const get_game_status = () => {
    axiosInstance
      .get("/get_game_status")
      .then((res) => {
        if (res.data.phase === "betting_phase") {
          setGlobalTimeNow(res.data.info);
          setbBettingPhase(true);
        } else if (res.data.phase === "game_phase") {
          setGlobalTimeNow(res.data.info);
          setLiveMultiplierSwitch(true);
        }
      })
      .catch((err) => {
        console.log("Get game status error (ignoring):", err.message);
        // Ignore errors on initial load
      });
  };

  const manual_cashout_early = () => {
    // Check socket connection
    if (!globalSocket || !isConnected) {
      setErrorMessage("Not connected to server");
      return;
    }

    // Allow multiple cashout attempts - don't block on pending state
    // This ensures users can spam click to cash out quickly

    setCashoutPending(true);

    // Timeout for socket response (5 seconds)
    let timeoutId = setTimeout(() => {
      setCashoutPending(false);
      // Only show timeout error if still have active bet
      if (betActive) {
        setErrorMessage("Cashout timeout - please try again");
      }
    }, 5000);

    // Emit socket event with acknowledgment callback
    globalSocket.emit("manual_cashout", (response) => {
      clearTimeout(timeoutId);
      setCashoutPending(false);

      if (
        response &&
        response.success &&
        response.balance &&
        response.bet_amount
      ) {
        playSoundEffect("cashout");
        // Use server-calculated profit instead of client calculation
        const profit = response.profit ? response.profit.toFixed(2) : "0.00";
        if (parseFloat(profit) > 0) {
          // profitToast(profit);
          if (betBtnRef.current && balanceRef.current) {
            const startRect = betBtnRef.current.getBoundingClientRect();
            const endRect = balanceRef.current.getBoundingClientRect();
            setCoinStartPos({
              x: startRect.left + startRect.width / 2,
              y: startRect.top + startRect.height / 2,
            });
            setCoinEndPos({
              x: endRect.left + endRect.width / 2,
              y: endRect.top + endRect.height / 2,
            });
          }
          setProfitDisplay(profit);
          setTimeout(() => {
            setProfitDisplay(null);
          }, 3000);
          setShowCoinsAnimation(true);
        }
        setUserData((prev) => ({ ...prev, balance: response.balance }));
        setBetActive(false);
      } else {
        // If error is "No active bet found", user already cashed out
        // Silently verify UI state instead of showing error
        if (response?.error === "No active bet found") {
          // Ensure UI reflects cashed out state
          if (betActive) {
            setBetActive(false);
          }
        } else {
          // Show other errors
          setErrorMessage(response?.error || "Cashout failed");
          console.error("Manual cashout failed:", response?.error);
        }
      }
    });
  };

  const auto_cashout_early = () => {
    // Check socket connection
    if (!globalSocket || !isConnected) {
      setErrorMessage("Not connected to server");
      return;
    }

    // Allow multiple cashout attempts - don't block on pending state
    // This ensures users can spam click to cash out quickly

    setCashoutPending(true);

    // Timeout for socket response (5 seconds)
    let timeoutId = setTimeout(() => {
      setCashoutPending(false);
      // Only show timeout error if still have active bet
      if (betActive) {
        setErrorMessage("Auto cashout timeout - please try again");
      }
    }, 5000);

    // Emit socket event with acknowledgment callback
    globalSocket.emit("auto_cashout", (response) => {
      clearTimeout(timeoutId);
      setCashoutPending(false);

      if (
        response &&
        response.success &&
        response.balance &&
        response.bet_amount
      ) {
        // Use server-calculated profit instead of client calculation
        const profit = response.profit ? response.profit.toFixed(2) : "0.00";
        if (parseFloat(profit) > 0) {
          // profitToast(profit);
          if (betBtnRef.current && balanceRef.current) {
            const startRect = betBtnRef.current.getBoundingClientRect();
            const endRect = balanceRef.current.getBoundingClientRect();
            setCoinStartPos({
              x: startRect.left + startRect.width / 2,
              y: startRect.top + startRect.height / 2,
            });
            setCoinEndPos({
              x: endRect.left + endRect.width / 2,
              y: endRect.top + endRect.height / 2,
            });
          }
          setProfitDisplay(profit);
          setTimeout(() => {
            setProfitDisplay(null);
          }, 3000);
          setShowCoinsAnimation(true);
        }
        setUserData((prev) => ({ ...prev, balance: response.balance }));
        setBetActive(false);
      } else {
        // If error is "No active bet found", user already cashed out
        // Silently verify UI state instead of showing error
        if (response?.error === "No active bet found") {
          // Ensure UI reflects cashed out state
          if (betActive) {
            setBetActive(false);
          }
        } else {
          // Show other errors
          setErrorMessage(response?.error || "Auto cashout failed");
          console.error("Auto cashout failed:", response?.error);
        }
      }
    });
  };

  const bet_next_round = () => {
    // Check if bet amount exceeds balance
    if (Number(betAmount) > Number(userData.balance)) {
      setErrorMessage("Insufficient balance");
      return;
    }

    playSoundEffect("bet-next-round");
    setErrorMessage(""); // Clear any previous error messages
    setbBetForNextRound(!bBetForNextRound);
  };

  const send_message_to_chatbox = () => {
    axiosInstance
      .post("/send_message_to_chatbox", {
        message_to_textbox: messageToTextBox,
      })
      .then((res) => {
        setMessageToTextBox("");
      });
  };

  const get_chat_history = () => {
    axiosInstance.get("/get_chat_history").then((res) => {
      setChatHistory(res.data);
    });
  };

  const retrieve_active_bettors_list = () => {
    axiosInstance.get("/retrieve_active_bettors_list").then((res) => {});
  };

  const retrieve_bet_history = () => {
    axiosInstance.get("/retrieve_bet_history").then((res) => {});
  };

  // Functions
  const handleKeyDownBetting = (e) => {
    if (e.key === "Enter") {
      if (bBettingPhase) {
        send_bet();
      } else {
        bet_next_round();
      }
    }
  };

  const handleKeyDownChat = (e) => {
    if (e.key === "Enter") {
      send_message_to_chatbox();
    }
  };

  // const verifyBetAmount = (text) => {
  //   const validated = text.match(/^(\d*\.{0,1}\d{0,2}$)/);
  //   const re = /^[0-9\b]+$/;

  //   if (text === "" || re.test(text)) {
  //     setBetAmount(text);
  //   }
  //   if (text > userData.balance) {
  //     setErrorMessage("Bet greater than balance");
  //   } else {
  //     setErrorMessage("");
  //   }
  // };

  const MAX_BET = 10000;

  const verifyBetAmount = (text) => {
    // remove commas (because the displayed value has them)
    const raw = text.replace(/,/g, "");

    // allow empty
    if (raw === "") {
      setBetAmount("");
      setErrorMessage("");
      return;
    }

    // only digits
    if (!/^\d+$/.test(raw)) return;

    // limit to 6 digits (999999)
    const limited = raw.slice(0, 6);

    const num = Number(limited);

    // optional: hard clamp (if you paste a bigger number)
    const clamped = Math.min(num, MAX_BET);

    setBetAmount(String(clamped));

    if (clamped > Number(userData.balance)) {
      setErrorMessage("Insufficient balance");
    } else {
      setErrorMessage("");
    }
  };

  const verifyMultiplierAmount = (text) => {
    const validated = text.match(/^(\d*\.{0,1}\d{0,2}$)/);
    if (validated) {
      setAutoPayoutMultiplier(text);
    }
  };

  const betIntervalRef = useRef(null);
  const betTimeoutRef = useRef(null);

  const handleIncreaseBet = () => {
    setBetAmount((prev) => {
      const val = parseFloat(prev);
      if (isNaN(val)) return 10;
      return Math.min(val + 10, MAX_BET);
    });
  };

  const handleDecreaseBet = () => {
    setBetAmount((prev) => {
      const val = parseFloat(prev);
      if (isNaN(val)) return 0;
      const newVal = val - 10;
      return newVal < 0 ? 0 : newVal;
    });
  };

  const handleBetButtonDown = (direction) => {
    let increment = 10;
    let intervalDelay = 150;

    // Initial increment
    if (direction === "increase") {
      handleIncreaseBet();
    } else {
      handleDecreaseBet();
    }

    // Start repeating after initial delay
    betTimeoutRef.current = setTimeout(() => {
      betIntervalRef.current = setInterval(() => {
        setBetAmount((prev) => {
          const val = parseFloat(prev) || 0;
          let newVal;

          if (direction === "increase") {
            newVal = val + increment;
            newVal = Math.min(newVal, MAX_BET);
          } else {
            newVal = val - increment;
            newVal = Math.max(newVal, 0);
          }

          return newVal;
        });
      }, intervalDelay);

      // Accelerate to 100s after 1.5 seconds
      betTimeoutRef.current = setTimeout(() => {
        clearInterval(betIntervalRef.current);
        increment = 100;
        intervalDelay = 100;

        betIntervalRef.current = setInterval(() => {
          setBetAmount((prev) => {
            const val = parseFloat(prev) || 0;
            let newVal;

            if (direction === "increase") {
              newVal = val + increment;
              newVal = Math.min(newVal, MAX_BET);
            } else {
              newVal = val - increment;
              newVal = Math.max(newVal, 0);
            }

            return newVal;
          });
        }, intervalDelay);

        // Accelerate to 1000s after another 1.5 seconds
        betTimeoutRef.current = setTimeout(() => {
          clearInterval(betIntervalRef.current);
          increment = 1000;
          intervalDelay = 100;

          betIntervalRef.current = setInterval(() => {
            setBetAmount((prev) => {
              const val = parseFloat(prev) || 0;
              let newVal;

              if (direction === "increase") {
                newVal = val + increment;
                newVal = Math.min(newVal, MAX_BET);
              } else {
                newVal = val - increment;
                newVal = Math.max(newVal, 0);
              }

              return newVal;
            });
          }, intervalDelay);
        }, 1500);
      }, 1500);
    }, 300);
  };

  const handleBetButtonUp = () => {
    if (betIntervalRef.current) {
      clearInterval(betIntervalRef.current);
      betIntervalRef.current = null;
    }
    if (betTimeoutRef.current) {
      clearTimeout(betTimeoutRef.current);
      betTimeoutRef.current = null;
    }
  };

  const multiplierIntervalRef = useRef(null);
  const multiplierTimeoutRef = useRef(null);

  const handleIncreaseMultiplier = () => {
    setAutoPayoutMultiplier((prev) => {
      const val = parseFloat(prev);
      if (isNaN(val)) return 1;
      const newVal = parseFloat((val + 0.1).toFixed(2));
      return newVal;
    });
  };

  const handleDecreaseMultiplier = () => {
    setAutoPayoutMultiplier((prev) => {
      const val = parseFloat(prev);
      if (isNaN(val)) return 1.1;
      const newVal = val - 0.1;
      return newVal < 1.1 ? 1.1 : parseFloat(newVal.toFixed(2));
    });
  };

  const handleMultiplierButtonDown = (direction) => {
    let increment = 0.1;
    let intervalDelay = 150;

    // Initial increment
    if (direction === "increase") {
      handleIncreaseMultiplier();
    } else {
      handleDecreaseMultiplier();
    }

    // Start repeating after initial delay
    multiplierTimeoutRef.current = setTimeout(() => {
      multiplierIntervalRef.current = setInterval(() => {
        setAutoPayoutMultiplier((prev) => {
          const val = parseFloat(prev) || 1.1;
          let newVal;

          if (direction === "increase") {
            newVal = parseFloat((val + increment).toFixed(2));
          } else {
            newVal = parseFloat((val - increment).toFixed(2));
            newVal = Math.max(newVal, 1.1);
          }

          return newVal;
        });
      }, intervalDelay);

      // Accelerate to 1.0 increments after 1.5 seconds
      multiplierTimeoutRef.current = setTimeout(() => {
        clearInterval(multiplierIntervalRef.current);
        increment = 1.0;
        intervalDelay = 100;

        multiplierIntervalRef.current = setInterval(() => {
          setAutoPayoutMultiplier((prev) => {
            const val = parseFloat(prev) || 1.1;
            let newVal;

            if (direction === "increase") {
              newVal = parseFloat((val + increment).toFixed(2));
            } else {
              newVal = parseFloat((val - increment).toFixed(2));
              newVal = Math.max(newVal, 1.1);
            }

            return newVal;
          });
        }, intervalDelay);

        // Accelerate to 10.0 increments after another 1.5 seconds
        multiplierTimeoutRef.current = setTimeout(() => {
          clearInterval(multiplierIntervalRef.current);
          increment = 10.0;
          intervalDelay = 100;

          multiplierIntervalRef.current = setInterval(() => {
            setAutoPayoutMultiplier((prev) => {
              const val = parseFloat(prev) || 1.1;
              let newVal;

              if (direction === "increase") {
                newVal = parseFloat((val + increment).toFixed(2));
              } else {
                newVal = parseFloat((val - increment).toFixed(2));
                newVal = Math.max(newVal, 1.1);
              }

              return newVal;
            });
          }, intervalDelay);
        }, 1500);
      }, 1500);
    }, 300);
  };

  const handleMultiplierButtonUp = () => {
    if (multiplierIntervalRef.current) {
      clearInterval(multiplierIntervalRef.current);
      multiplierIntervalRef.current = null;
    }
    if (multiplierTimeoutRef.current) {
      clearTimeout(multiplierTimeoutRef.current);
      multiplierTimeoutRef.current = null;
    }
  };

  // Define Toasts
  const profitToast = (profit) => {
    toast.success(
      `Profit: +${Number(profit).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
        progress: undefined,
        theme: "dark",
        transition: Slide,
      },
    );
  };

  const temp_time = Date.now();

  useEffect(() => {
    const temp_interval = setInterval(() => {
      setChartSwitch(false);
      sendToChart();
    }, 33);

    return () => {
      clearInterval(temp_interval);
      setChartSwitch(true);
    };
  }, [chartSwitch]);

  const gradients = { line: null, fill: null, w: 0, h: 0 };

  function getLineGradient(chart) {
    const { ctx, chartArea, width } = chart;

    // Use chartArea when available, otherwise fall back to full chart width
    const left = chartArea?.left ?? 0;
    const right = chartArea?.right ?? width;

    // Recreate only if size changed (prevents flicker + saves work)
    if (!gradients.line || gradients.w !== width) {
      const g = ctx.createLinearGradient(left, 0, right, 0);
      g.addColorStop(0, "#2CFF37");
      g.addColorStop(1, "#FF6D19");
      gradients.line = g;
      gradients.w = width;
    }

    return gradients.line;
  }

  function getFillGradient(chart) {
    const { ctx, chartArea, height } = chart;

    // Use chartArea when available, otherwise fall back to full chart height
    const top = chartArea?.top ?? 0;
    const bottom = chartArea?.bottom ?? height;

    if (!gradients.fill || gradients.h !== height) {
      const g = ctx.createLinearGradient(0, bottom, 0, top);
      g.addColorStop(0, "rgba(44, 255, 55, 0.25)");
      g.addColorStop(1, "rgba(89, 214, 255, 0.25)");
      gradients.fill = g;
      gradients.h = height;
    }

    return gradients.fill;
  }

  // Chart Data
  const sendToChart = () => {
    setChartData({
      labels: timeCount_xaxis.current,
      datasets: [
        {
          data: multiplierCount.current,

          // FILL gradient (bottom->top) — keep this if you want area fill
          backgroundColor: "rgba(44, 255, 55, 0.25)",
          borderColor: (context) => {
            const { chart } = context;
            const { ctx, chartArea } = chart;
            if (!chartArea) return null;

            // left -> right gradient so the end of the line becomes blue
            const gradient = ctx.createLinearGradient(
              chartArea.left,
              0,
              chartArea.right,
              0,
            );
            gradient.addColorStop(0, "#2CFF37");
            gradient.addColorStop(0.7, "#59D6FF");
            return gradient;
          },

          fill: true,
          pointRadius: 0,
          borderWidth: 4,
          tension: 0.4,
          cubicInterpolationMode: "monotone",
        },
      ],
    });
    setChartOptions({
      layout: {
        padding: {
          top: 60,
          right: 80,
          left: 0,
          bottom: 0,
        },
      },
      events: [],
      maintainAspectRatio: false,
      elements: {
        line: {
          tension: 0.4,
        },
      },
      scales: {
        yAxes: {
          type: "linear",
          border: {
            display: false,
            color: "rgba(255, 255, 255,0)",
          },
          title: {
            display: false,
            text: "value",
          },
          min: 1,
          max: Math.max(
            1,
            (parseFloat(liveMultiplier) || 0) * 1.3 +
              0.15 *
                liveMultiplier *
                (Math.sin(
                  (parseFloat(gamePhaseTimeElapsed) || 0) * 0.5 +
                    360 * randomSeedPlaneAnimation,
                ) *
                  0.7 +
                  Math.sin(
                    (parseFloat(gamePhaseTimeElapsed) || 0) +
                      360 * randomSeedPlaneAnimation,
                  ) *
                    0.3),
          ),
          ticks: {
            display: false,
            color: "rgba(255, 255, 255,0.4)",
            count: 5,
            callback: function (value, index, values) {
              return parseFloat(value).toFixed(0);
            },
            padding: 0,
          },
          grid: {
            display: false,
            color: "rgba(255, 255, 255,0.4)",
            drawTicks: false,
          },
        },
        xAxes: {
          border: {
            display: false,
            color: "rgba(255, 255, 255,0)",
          },
          type: "linear",
          title: {
            display: false,
            text: "value",
          },
          min: 0,
          max: Math.max(
            5,
            1 +
              (gamePhaseTimeElapsed || 0) * 1.3 +
              0.3 *
                gamePhaseTimeElapsed *
                (Math.sin((parseFloat(gamePhaseTimeElapsed) || 0) * 0.2) * 0.6 +
                  Math.sin((parseFloat(gamePhaseTimeElapsed) || 0) * 0.7) *
                    0.4),
          ),
          ticks: {
            display: false,
            color: "rgba(255, 255, 255,0.4)",
            count: 5,
            stepSize: 5,
            callback: function (value, index, values) {
              return parseFloat(value).toFixed(0);
            },
            padding: 0,
          },
          grid: {
            display: false,
            color: "rgba(255, 255, 255,0.4)",
            drawTicks: false,
          },
        },
      },
      plugins: {
        legend: { display: false },
      },
      animation: false,
      options: {
        animation: false,
      },
    });
  };

  const maskName = useCallback((name = "") => {
    const s = String(name).trim();
    if (s.length <= 2) return s; // "A", "AB"
    return s[0] + "*".repeat(3) + s[s.length - 1];
  }, []);

  const handleCoinsAnimationComplete = useCallback(() => {
    setShowCoinsAnimation(false);
  }, []);

  //JSX
  return (
    <div className="App">
      <div>
        {showCoinsAnimation && (
          <CoinsAnimation
            onComplete={handleCoinsAnimationComplete}
            startPos={coinStartPos}
            endPos={coinEndPos}
          />
        )}
        <ToastContainer />
      </div>

      <nav className="navbar flex items-center justify-center pr-3 pl-3">
        <div className="flex items-center justify-between w-full ">
          {/* <span className="logo">Skyline</span> */}
          <div className="flex items-center pt-1">
            <img
              src="/LogoGroup.png"
              alt="Skyline logo"
              className="select-none skyline-logo"
            />
          </div>
          <ul className="nav">
            {userData && (
              <div className="flex items-center gap-3 relative">
                {profitDisplay && (
                  <div className="profit-text">
                    +
                    {Number(profitDisplay ?? 0).toLocaleString("en-US", {
                      minimumFractionDigits: 0,
                    })}
                  </div>
                )}
                <div className="select-none balance-div" ref={balanceRef}>
                  <div className="balance-svg">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                    >
                      <path
                        d="M6 8H0V6H6V0H8V6H14V8H8V14H6V8Z"
                        fill="#898989"
                      />
                    </svg>
                  </div>
                  {Number(userData?.balance ?? 0).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </div>
              </div>
            )}
          </ul>
        </div>
      </nav>

      <div className="flex flex-col md:flex-row">
        <div className="flex flex-col md:w-1/2">
          <div className="section-one">
            <CrashHistoryRow crashHistory={crashHistory} />

            {/* Chart */}
            <div className="px-1.5 w-full gap-2">
              <div className="chart-section">
                {
                  <div className="effects-box">
                    <div
                      className="basically-the-graph"
                      style={{
                        height: "100%",
                        width: "100%",
                        position: "absolute",
                        top: "0",
                      }}
                    >
                      {chartData ? (
                        <SomeChart
                          chartData={chartData}
                          chartOptions={chartOptions}
                          isLive={liveMultiplierSwitch}
                        />
                      ) : (
                        ""
                      )}
                    </div>
                    {(() => {
                      if (!isConnected) {
                        return (
                          <div className="diconnected-div">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M12 22C10.6333 22 9.34167 21.7375 8.125 21.2125C6.90833 20.6875 5.84583 19.9708 4.9375 19.0625C4.02917 18.1542 3.3125 17.0917 2.7875 15.875C2.2625 14.6583 2 13.3667 2 12C2 10.6167 2.2625 9.32083 2.7875 8.1125C3.3125 6.90417 4.02917 5.84583 4.9375 4.9375C5.84583 4.02917 6.90833 3.3125 8.125 2.7875C9.34167 2.2625 10.6333 2 12 2C12.2833 2 12.5208 2.09583 12.7125 2.2875C12.9042 2.47917 13 2.71667 13 3C13 3.28333 12.9042 3.52083 12.7125 3.7125C12.5208 3.90417 12.2833 4 12 4C9.78333 4 7.89583 4.77917 6.3375 6.3375C4.77917 7.89583 4 9.78333 4 12C4 14.2167 4.77917 16.1042 6.3375 17.6625C7.89583 19.2208 9.78333 20 12 20C14.2167 20 16.1042 19.2208 17.6625 17.6625C19.2208 16.1042 20 14.2167 20 12C20 11.7167 20.0958 11.4792 20.2875 11.2875C20.4792 11.0958 20.7167 11 21 11C21.2833 11 21.5208 11.0958 21.7125 11.2875C21.9042 11.4792 22 11.7167 22 12C22 13.3667 21.7375 14.6583 21.2125 15.875C20.6875 17.0917 19.9708 18.1542 19.0625 19.0625C18.1542 19.9708 17.0958 20.6875 15.8875 21.2125C14.6792 21.7375 13.3833 22 12 22Z"
                                fill="black"
                                fillOpacity="0.6"
                              />
                            </svg>
                            Connection Error, Trying to Re-connect
                          </div>
                        );
                      } else if (bBettingPhase) {
                        return (
                          <div className="counting-value">
                            <CountdownAnimation />
                          </div>
                        );
                      } else {
                        return (
                          <div className="crash-value">
                            <h1
                              className={` select-none ${
                                !liveMultiplierSwitch &&
                                liveMultiplier !== "Starting..." &&
                                liveMultiplier !== "CONNECTING..." &&
                                liveMultiplier !== "CONNECTED"
                                  ? "multipler_crash_value_message"
                                  : liveMultiplierSwitch &&
                                      liveMultiplier !== "Starting..." &&
                                      liveMultiplier !== "CONNECTING..." &&
                                      liveMultiplier !== "CONNECTED"
                                    ? "multipler_crash_value"
                                    : ""
                              }`}
                            >
                              {liveMultiplier !== "Starting..." &&
                              liveMultiplier !== "CONNECTING..." &&
                              liveMultiplier !== "CONNECTED"
                                ? liveMultiplier + "x"
                                : liveMultiplier === "CONNECTING..." &&
                                    liveMultiplier !== "CONNECTED"
                                  ? "CONNECTING..."
                                  : ""}
                            </h1>
                          </div>
                        );
                      }
                    })()}

                    {isConnected && (
                      <>
                        {randomAvatarUsers.length > 0 ? (
                          randomAvatarUsers.map((user, index) => {
                            const positions = [
                              { zIndex: "z-20", left: "left-1" },
                              { zIndex: "z-30", left: "left-4" },
                              { zIndex: "z-40", left: "left-7" },
                            ];
                            return (
                              <div className="live-betting-users">
                                <img
                                  key={user.the_user_id}
                                  src={`/${user.the_avatar}`}
                                  className={`absolute ${positions[index].zIndex} ${positions[index].left} w-7 h-7 rounded-full`}
                                  alt={user.the_username}
                                />

                                <p className="text-base text-white absolute right-2 font-semibold select-none">
                                  {activePlayerCount}
                                </p>
                              </div>
                            );
                          })
                        ) : (
                          // Show placeholder avatars when no users
                          <div className="empty-user-state "></div>
                        )}
                      </>
                    )}
                  </div>
                }
              </div>
              {/* Betting Section  */}
              <div className="betting-section">
                <div className="toggle-tabs" role="tablist" aria-label="Mode">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={mode === "manual"}
                    className={`flex items-center gap-2 justify-center text-white/85 font-semibold toggle-tab ${mode === "manual" ? "is-active" : ""}`}
                    onClick={() => {
                      setMode("manual");
                      triggerHaptic();
                      playSoundEffect("toggle-switch");
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="21"
                      viewBox="0 0 18 21"
                      fill="none"
                    >
                      <path
                        d="M8 7.275V6.85C7.26667 6.63333 6.66667 6.22083 6.2 5.6125C5.73333 5.00417 5.5 4.3 5.5 3.5C5.5 2.53333 5.84167 1.70833 6.525 1.025C7.20833 0.341667 8.03333 0 9 0C9.96667 0 10.7917 0.341667 11.475 1.025C12.1583 1.70833 12.5 2.53333 12.5 3.5C12.5 4.3 12.2667 5.00417 11.8 5.6125C11.3333 6.22083 10.7333 6.63333 10 6.85V7.275L17 11.3C17.3167 11.4833 17.5625 11.7292 17.7375 12.0375C17.9125 12.3458 18 12.6833 18 13.05V14.95C18 15.3167 17.9125 15.6542 17.7375 15.9625C17.5625 16.2708 17.3167 16.5167 17 16.7L10 20.725C9.68333 20.9083 9.35 21 9 21C8.65 21 8.31667 20.9083 8 20.725L1 16.7C0.683333 16.5167 0.4375 16.2708 0.2625 15.9625C0.0875 15.6542 0 15.3167 0 14.95V13.05C0 12.6833 0.0875 12.3458 0.2625 12.0375C0.4375 11.7292 0.683333 11.4833 1 11.3L8 7.275ZM3.8 12L9 15L14.2 12L10 9.575V13H8V9.575L3.8 12Z"
                        fill="#F9F4DA"
                        fillOpacity={mode === "manual" ? 1 : 0.35}
                      />
                    </svg>
                    <span className="pt-0.5 select-none">Manual</span>
                  </button>

                  <button
                    type="button"
                    role="tab"
                    aria-selected={mode === "auto"}
                    className={`flex items-center gap-2 justify-center text-white/85 font-semibold toggle-tab ${mode === "auto" ? "is-active" : ""}`}
                    onClick={() => {
                      setMode("auto");
                      triggerHaptic();
                      playSoundEffect("toggle-switch");
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                    >
                      <path
                        d="M4.5 15C4.91667 15 5.27083 14.8542 5.5625 14.5625C5.85417 14.2708 6 13.9167 6 13.5C6 13.0833 5.85417 12.7292 5.5625 12.4375C5.27083 12.1458 4.91667 12 4.5 12C4.08333 12 3.72917 12.1458 3.4375 12.4375C3.14583 12.7292 3 13.0833 3 13.5C3 13.9167 3.14583 14.2708 3.4375 14.5625C3.72917 14.8542 4.08333 15 4.5 15ZM4.5 6C4.91667 6 5.27083 5.85417 5.5625 5.5625C5.85417 5.27083 6 4.91667 6 4.5C6 4.08333 5.85417 3.72917 5.5625 3.4375C5.27083 3.14583 4.91667 3 4.5 3C4.08333 3 3.72917 3.14583 3.4375 3.4375C3.14583 3.72917 3 4.08333 3 4.5C3 4.91667 3.14583 5.27083 3.4375 5.5625C3.72917 5.85417 4.08333 6 4.5 6ZM9 10.5C9.41667 10.5 9.77083 10.3542 10.0625 10.0625C10.3542 9.77083 10.5 9.41667 10.5 9C10.5 8.58333 10.3542 8.22917 10.0625 7.9375C9.77083 7.64583 9.41667 7.5 9 7.5C8.58333 7.5 8.22917 7.64583 7.9375 7.9375C7.64583 8.22917 7.5 8.58333 7.5 9C7.5 9.41667 7.64583 9.77083 7.9375 10.0625C8.22917 10.3542 8.58333 10.5 9 10.5ZM13.5 15C13.9167 15 14.2708 14.8542 14.5625 14.5625C14.8542 14.2708 15 13.9167 15 13.5C15 13.0833 14.8542 12.7292 14.5625 12.4375C14.2708 12.1458 13.9167 12 13.5 12C13.0833 12 12.7292 12.1458 12.4375 12.4375C12.1458 12.7292 12 13.0833 12 13.5C12 13.9167 12.1458 14.2708 12.4375 14.5625C12.7292 14.8542 13.0833 15 13.5 15ZM13.5 6C13.9167 6 14.2708 5.85417 14.5625 5.5625C14.8542 5.27083 15 4.91667 15 4.5C15 4.08333 14.8542 3.72917 14.5625 3.4375C14.2708 3.14583 13.9167 3 13.5 3C13.0833 3 12.7292 3.14583 12.4375 3.4375C12.1458 3.72917 12 4.08333 12 4.5C12 4.91667 12.1458 5.27083 12.4375 5.5625C12.7292 5.85417 13.0833 6 13.5 6ZM2 18C1.45 18 0.979167 17.8042 0.5875 17.4125C0.195833 17.0208 0 16.55 0 16V2C0 1.45 0.195833 0.979167 0.5875 0.5875C0.979167 0.195833 1.45 0 2 0H16C16.55 0 17.0208 0.195833 17.4125 0.5875C17.8042 0.979167 18 1.45 18 2V16C18 16.55 17.8042 17.0208 17.4125 17.4125C17.0208 17.8042 16.55 18 16 18H2Z"
                        fill="#F9F4DA"
                        fillOpacity={mode === "auto" ? 1 : 0.35}
                      />
                    </svg>

                    <span className="pt-0.5 select-none">Auto</span>
                  </button>
                </div>
                {userData ? (
                  <div className="flex flex-col px-0.5">
                    <div className="grid grid-cols-2 pt-3 gap-3">
                      {/* <h1 className="makeshift-input-group"> Bet Amount</h1> */}
                      <div className="inbox-div">
                        <div className="flex flex-col gap-1.5 mb-1.5">
                          <div
                            className={`relative flex items-center justify-between bet-amount-div input_box_div ${
                              betActive || bBetForNextRound ? "disabled" : null
                            } ${isInputOneFocusVisible ? "bet-focus-visible" : ""}`}
                          >
                            <button
                              type="button"
                              role="tab"
                              className="absolute left-0 rounded-full  flex items-center justify-center plus-minus-btn"
                              onMouseDown={() => {
                                handleBetButtonDown("decrease");
                                triggerHaptic();
                                playSoundEffect("decrement");
                              }}
                              onMouseUp={handleBetButtonUp}
                              onMouseLeave={handleBetButtonUp}
                              onTouchStart={() => {
                                handleBetButtonDown("decrease");
                                triggerHaptic();
                                playSoundEffect("decrement");
                              }}
                              onTouchEnd={handleBetButtonUp}
                              onTouchCancel={handleBetButtonUp}
                              disabled={betActive || bBetForNextRound}
                            >
                              <MinusIcon
                                size={24}
                                color={
                                  betActive || bBetForNextRound
                                    ? "#000"
                                    : "#fff"
                                }
                              />
                            </button>
                            <input
                              className="input_box border-none"
                              placeholder=""
                              onChange={(e) => verifyBetAmount(e.target.value)}
                              value={Number(betAmount).toLocaleString("en-US", {
                                maximumFractionDigits: 0,
                              })}
                              onFocus={(e) => {
                                // treat keyboard focus as focus-visible
                                if (e.target.matches(":focus-visible"))
                                  setIsInputOneFocusVisible(true);
                              }}
                              onBlur={() => setIsInputOneFocusVisible(false)}
                              inputMode="numeric"
                              disabled={
                                betActive || bBetForNextRound
                                  ? "disabled"
                                  : null
                              }
                              onKeyDown={handleKeyDownBetting}
                            />
                            <button
                              type="button"
                              role="tab"
                              className="absolute right-0 rounded-full  flex items-center justify-center plus-minus-btn"
                              onMouseDown={() => {
                                handleBetButtonDown("increase");
                                triggerHaptic();
                                playSoundEffect("increment");
                              }}
                              onMouseUp={handleBetButtonUp}
                              onMouseLeave={handleBetButtonUp}
                              onTouchStart={() => {
                                handleBetButtonDown("increase");
                                triggerHaptic();
                                playSoundEffect("increment");
                              }}
                              onTouchEnd={handleBetButtonUp}
                              onTouchCancel={handleBetButtonUp}
                              disabled={betActive || bBetForNextRound}
                            >
                              <PlusIcon
                                size={24}
                                color={
                                  betActive || bBetForNextRound
                                    ? "#000"
                                    : "#fff"
                                }
                              />
                            </button>
                          </div>
                          <div
                            style={{
                              color: "#FF6262",
                              fontWeight: "500",
                              fontSize: "12px",
                              fontFamily: "Inter",
                            }}
                          >
                            {errorMessage}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-0.5 gap-y-1">
                          {[100, 200, 500, 1000].map((val) => (
                            <button
                              key={val}
                              type="button"
                              role="tab"
                              className={`multiply-button`}
                              onClick={() => {
                                let current = parseInt(betAmount) || 0;
                                let next = current + val;
                                if (next > 10000) next = 10000;
                                verifyBetAmount(next.toString());
                                setLastQuickBetValue(val);
                                triggerHaptic();
                                playSoundEffect("quick-bet");
                              }}
                              disabled={betActive || bBetForNextRound}
                            >
                              {Number(val).toLocaleString("en-US", {
                                maximumFractionDigits: 0,
                              })}
                            </button>
                          ))}
                          {/* <input
                      className="input_box"
                      placeholder="Payout Multiplier"
                      onChange={(e) => verifyMultiplierAmount(e.target.value)}
                      onKeyDown={handleKeyDownBetting}
                      value={autoPayoutMultiplier}
                      disabled={betActive ? "disabled" : null}
                    /> */}
                        </div>
                      </div>
                      <div className="bet-div" ref={betBtnRef}>
                        {bBettingPhase && !betActive ? (
                          <button
                            className={`css-button css-button-3d css-button-3d--grey bet-btn ${betPending ? "bet-btn-pending" : ""}`}
                            disabled={
                              betActive || bBetForNextRound || betPending
                            }
                            onClick={() => {
                              send_bet();
                              triggerHaptic();
                            }}
                          >
                            <span className="title">
                              {betPending ? "Betting..." : "Bet"}
                            </span>
                            <span>
                              Rs.
                              {Number(betAmount).toLocaleString("en-US", {
                                maximumFractionDigits: 0,
                              })}
                            </span>
                          </button>
                        ) : (
                          // : bBettingPhase && betActive ? (
                          //   <button
                          //     class="css-button css-button-3d css-button-3d--grey cancel-btn"
                          //     onClick={cancel_bet}
                          //   >
                          //     <span className="title">CANCEL BET</span>
                          //     <span>
                          //       Refund: Rs.
                          //       {Number(betAmount).toLocaleString("en-US", {
                          //         maximumFractionDigits: 0,
                          //       })}
                          //     </span>
                          //   </button>
                          // )
                          <>
                            {betActive ? (
                              <button
                                class={`css-button css-button-3d css-button-3d--grey ${betActive && liveMultiplier > 1 ? "cashout-btn" : "cancel-btn"}`}
                                onClick={() => {
                                  if (betActive && liveMultiplier > 1) {
                                    manual_cashout_early();
                                  } else {
                                    cancel_bet();
                                  }
                                  triggerHaptic();
                                  playSoundEffect("button-click");
                                }}
                              >
                                {betActive && liveMultiplier > 1 ? (
                                  <>
                                    <span className="title">Cashout</span>
                                    <span>
                                      Rs.
                                      {Number(
                                        liveMultiplier * betAmount,
                                      ).toLocaleString("en-US", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}
                                    </span>
                                  </>
                                ) : (
                                  <span className="title">CANCEL</span>
                                )}
                              </button>
                            ) : (
                              <button
                                class={`css-button css-button-3d css-button-3d--grey ${bBetForNextRound ? "bet_for_next_round_active cancel-text-btn" : "bet-btn"}`}
                                onClick={() => {
                                  bet_next_round();
                                  triggerHaptic();
                                  playSoundEffect("button-click");
                                }}
                              >
                                {bBetForNextRound ? (
                                  <>
                                    <span className="title">CANCEL</span>
                                    <span>Waiting for next round</span>
                                  </>
                                ) : (
                                  <>
                                    <span className="title">Bet</span>
                                    <span>
                                      Rs.
                                      {Number(betAmount).toLocaleString(
                                        "en-US",
                                        { maximumFractionDigits: 0 },
                                      )}
                                    </span>
                                  </>
                                )}
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    {mode === "auto" && (
                      <div className="flex items-center gap-5 mt-3.5 auto-cashout-section">
                        <div className="flex items-center gap-2">
                          <p
                            className="min-w-max text-white text-left select-none auto-text pr-2"
                            style={{
                              color: autoCashoutEnabled
                                ? "#FAFACD"
                                : "rgba(255, 255, 255, 0.47)",
                              textShadow: autoCashoutEnabled
                                ? "0 0 3px rgba(0, 0, 0, 0.50), 0 0 15px rgba(255, 255, 255, 0.60), 0 0 4px rgba(255, 255, 255, 0.60)"
                                : "",
                            }}
                          >
                            Auto<br></br>Cashout
                          </p>
                          <label className="switch">
                            <input
                              type="checkbox"
                              checked={autoCashoutEnabled}
                              onChange={(e) => {
                                setAutoCashoutEnabled(e.target.checked);
                                triggerHaptic();
                                playSoundEffect("toggle-switch");
                              }}
                            />
                            <span
                              className={`slider round${autoCashoutEnabled ? " slider-on" : ""}`}
                            ></span>
                          </label>
                        </div>
                        {/* <input
                          className="auto-cashout-input"
                          placeholder="Payout Multiplier"
                          onChange={(e) =>
                            verifyMultiplierAmount(e.target.value)
                          }
                          onKeyDown={handleKeyDownBetting}
                          value={autoPayoutMultiplier}
                          disabled={
                            betActive || !autoCashoutEnabled ? "disabled" : null
                          }
                        /> */}
                        <div
                          className={`relative flex items-center justify-between bet-amount-div bet-w ${
                            betActive || !autoCashoutEnabled ? "disabled" : null
                          } ${isInputTwoFocusVisible ? "bet-focus-visible" : ""}`}
                        >
                          <button
                            type="button"
                            role="tab"
                            className="absolute left-0 rounded-full  flex items-center justify-center plus-minus-btn"
                            onMouseDown={() => {
                              handleMultiplierButtonDown("decrease");
                              triggerHaptic();
                              playSoundEffect("decrement");
                            }}
                            onMouseUp={handleMultiplierButtonUp}
                            onMouseLeave={handleMultiplierButtonUp}
                            onTouchStart={() => {
                              handleMultiplierButtonDown("decrease");
                              triggerHaptic();
                              playSoundEffect("decrement");
                            }}
                            onTouchEnd={handleMultiplierButtonUp}
                            onTouchCancel={handleMultiplierButtonUp}
                            disabled={betActive || !autoCashoutEnabled}
                          >
                            <MinusIcon
                              size={24}
                              color={
                                betActive || !autoCashoutEnabled
                                  ? "#000"
                                  : "#fff"
                              }
                            />
                          </button>
                          <input
                            className="input_box border-none"
                            placeholder=""
                            onChange={(e) =>
                              verifyMultiplierAmount(e.target.value)
                            }
                            value={autoPayoutMultiplier}
                            inputMode="numeric"
                            disabled={
                              betActive || !autoCashoutEnabled
                                ? "disabled"
                                : null
                            }
                            onKeyDown={handleKeyDownBetting}
                            onFocus={(e) => {
                              // treat keyboard focus as focus-visible
                              if (e.target.matches(":focus-visible"))
                                setIsInputTwoFocusVisible(true);
                            }}
                            onBlur={() => setIsInputTwoFocusVisible(false)}
                          />
                          <button
                            type="button"
                            role="tab"
                            className="absolute right-0 rounded-full  flex items-center justify-center plus-minus-btn"
                            onMouseDown={() => {
                              handleMultiplierButtonDown("increase");
                              triggerHaptic();
                              playSoundEffect("increment");
                            }}
                            onMouseUp={handleMultiplierButtonUp}
                            onMouseLeave={handleMultiplierButtonUp}
                            onTouchStart={() => {
                              handleMultiplierButtonDown("increase");
                              triggerHaptic();
                              playSoundEffect("increment");
                            }}
                            onTouchEnd={handleMultiplierButtonUp}
                            onTouchCancel={handleMultiplierButtonUp}
                            disabled={betActive || !autoCashoutEnabled}
                          >
                            <PlusIcon
                              size={24}
                              color={
                                betActive || !autoCashoutEnabled
                                  ? "#000"
                                  : "#fff"
                              }
                            />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mt-5">
                    <p>Loading...</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* Live Bets Tracker  */}
        {/* <div
              className="toggle-tabs mb-2"
              role="tablist"
              aria-label="ViewMode"
            >
              <button
                type="button"
                role="tab"
                aria-selected={viewMode === "all"}
                className={`text-white/85 font-semibold toggle-tab ${viewMode === "all" ? "is-active" : ""}`}
                onClick={() => setViewMode("all")}
              >
                All Bets
              </button>

              <button
                type="button"
                role="tab"
                aria-selected={viewMode === "previous"}
                className={`text-white/85 font-semibold toggle-tab ${viewMode === "previous" ? "is-active" : ""}`}
                onClick={() => setViewMode("previous")}
              >
                Previous
              </button>

              <button
                type="button"
                role="tab"
                aria-selected={viewMode === "top"}
                className={`text-white/85 font-semibold toggle-tab ${viewMode === "top" ? "is-active" : ""}`}
                onClick={() => setViewMode("top")}
              >
                Top
              </button>
            </div> */}
        <div className="active-bet-table-section">
          <ul class="active-bet-table mb-1">
            <li className="grid grid-cols-7 active-bet-table-header">
              <div class="col-span-2 col col-1 flex items-center  select-none">
                Player
              </div>
              <div class="col-span-2 col col-2 flex items-center justify-center select-none">
                Bet LKR
              </div>
              <div class="col col-3 flex items-center justify-center select-none mulipler-header">
                X
              </div>
              <div class="col-span-2 col col-4 flex items-center justify-end select-none pr-2">
                Win LKR
              </div>
            </li>
          </ul>
          <div>
            {sortedBettingTable.length > 0 ? (
              <>
                {sortedBettingTable.map((bet) => {
                  // Stable key from username + bet amount
                  const stableKey = `${bet.the_username}-${bet.bet_amount}`;

                  return (
                    <ActiveBetRow
                      key={stableKey}
                      bet={bet}
                      currentUsername={userData?.username}
                      maskName={maskName}
                    />
                  );
                })}
              </>
            ) : null}

            <div class="container-crash-history">
              <ul class="active-bet-table">
                {tenNumbers.map((someNumber, index) => {
                  return (
                    <div
                      className="row-bet-wrapper"
                      key={`placeholder-${index}`}
                    >
                      <li class={"grid grid-cols-7 table-row-blue "}>
                        <div class="col-span-2 flex items-center gap-1 select-none pl-2">
                          --
                        </div>
                        <div class="col-span-2 flex items-center justify-center select-none">
                          --
                        </div>
                        <div class="flex items-center justify-center select-none ">
                          --
                        </div>
                        <div class="col-span-2 flex items-center justify-end select-none pr-2">
                          --
                        </div>
                      </li>
                    </div>
                  );
                })}
              </ul>
            </div>
            <div></div>
          </div>
        </div>

        {/* History  */}

        {/* <div className="history-section">
              <div class="container-crash-history">
                <ul class="history-table">
               <li class="history-table-header">
                <div class="col col-1">Game Id</div>
                <div class="col col-2">Crash At</div>
                <div class="col col-3">Streak</div>
              </li> 
              <li>
                  {crashHistory
                    .slice(0)
                    .reverse()
                    .map((crash, index, array) => {
                      return (
                        <div key={uuidv4()}>
                          <div class="col col-1">{roundIdList[index]} </div>
                          <div
                            class={
                              crash >= 2 ? "table-row-blue" : "table-row-red"
                            }
                          >
                            {crash}x
                          </div>
                          <div class="col col-3">{streakList[index]}</div>
                        </div>
                      );
                    })}
                </li> 
              </ul>
              </div>
            </div> */}
        {/* Chat */}
        {/* <div className="grid-elements">
          Chat <br />
          <div className="chat-box-wrapper">
            <div className="chat-box-rectangle">
              {chatHistory ? (
                <>
                  {chatHistory.map((message) => {
                    return (
                      <div className="individual-chat-message" key={uuidv4()}>
                        <span className="message_top">
                          {message.the_username}{" "}
                        </span>
                        <span className="message_top_time">
                          {message.the_time} -&nbsp;
                          {message.the_date}{" "}
                        </span>{" "}
                        <br />
                        <span className="message_bottom">
                          {message.message_body}
                        </span>
                      </div>
                    );
                  })}
                </>
              ) : (
                <h1>Loading Chat history </h1>
              )}
            </div>
          </div>
          {userData ? (
            <>
              <input
                className="input_box_for_chat"
                placeholder="Send A Message"
                onChange={(e) => setMessageToTextBox(e.target.value)}
                value={messageToTextBox}
                onKeyDown={handleKeyDownChat}
              />
              <br />
            </>
          ) : (
            <h3>Log in to send a chat message</h3>
          )}
        </div> */}
      </div>
    </div>
  );
}

export default App;
